/**
 * Доля хвоста публичного рендерера: вид `multi_step_question` (вопрос с несколькими ответами).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'multi_step_question'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderMultiStepQuestion(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
// WEB-1651-3: цвет самой отметки нативных чекбоксов/радио (accent-color).
import { resolveOptionAccent } from '../optionAccentColor';
// WEB-1672: цвет подписей вариантов — тот же слой, что у single/multiple_choice
// в соседнем рендерере (одна логика на оба пути).
import {
  optionTextColorOverride,
  optionFontSizeOverride,
  optionShadowOverride,
  optionGapOverride,
  optionContentGapOverride,
  optionRadiusOverride,
  optionIndicatorHidden,
  optionIndicatorAlignOverride,
  optionRowIndicatorOverride,
  OPTION_INDICATOR_HIDDEN_STYLE,
  optionBackgroundOverride,
  optionPaddingOverride,
  optionBorderOverride,
  optionFontWeightOverride,
  optionMinHeightOverride,
} from '../optionRowStyle';
import { optionCellOverride, optionListGridOverride } from '../optionGrid';
import { OptionRowContent } from '../OptionRowContent';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';


export function renderMultiStepQuestion(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, primaryColor, richText, hasText, optionDisplay, values, setValue } = ctx;
  return (
    <div style={{ marginBottom: 16, color: primaryColor }}>
      {hasText(block.content) && (
        <p
          style={{
            fontWeight: 600,
            marginBottom: 12,
            color: primaryColor,
            ...(block.fontSize ? { fontSize: block.fontSize } : {}),
          }}
        >
          {richText(block.content)}
        </p>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          // Слово владельца 20.10.2026: свой промежуток между вариантами.
          ...optionGapOverride(block),
          // Задание stylepack/option-grid: 2–3 столбца сеткой.
          ...optionListGridOverride(block),
        }}
      >
        {(block.options ?? []).map((opt) => {
          // WEB-1674: тот же признак выбора, что уже стоит у input ниже
          // (checked) — накладкам заливки и цвета нужно знать состояние.
          const isSelected = (values[block.id] ?? '')
            .split(',')
            .filter(Boolean)
            .includes(opt.id);
          return (
          <label
            key={opt.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              // Задание stylepack/option-content-gap: свой зазор значка и
              // текста — ПОСЛЕ зашитых 10; пусто — {}.
              ...optionContentGapOverride(block),
              padding: '10px 14px',
              border: '2px solid var(--color-border-primary, #f0f0f0)',
              borderRadius: 'var(--border-radius-md, 8px)',
              cursor: 'pointer',
              color: primaryColor,
              // WEB-1672: накладка цвета подписей вариантов — ПОСЛЕ
              // строки выше, поэтому она сильнее цвета страницы. Пустое
              // поле спредит {} — разметка старой воронки та же.
              ...optionTextColorOverride(block, isSelected),
              // WEB-1673: свой кегль вариантов. Здесь строка варианта
              // своего размера не имела вовсе — пустое поле по-прежнему
              // не добавляет в разметку ничего.
              ...optionFontSizeOverride(block),
              // WEB-1674: своя заливка варианта. Собственной подложки у
              // этой строки не было вовсе — пустые поля ничего не
              // добавляют, разметка старой воронки та же.
              ...optionBackgroundOverride(block, isSelected),
              // Слово владельца 20.10.2026: своё скругление и тень строки.
              // Кольца выбора у этой строки нет (рамка своя) — тень одна.
              ...optionRadiusOverride(block),
              ...optionShadowOverride(block, false, isSelected),
              ...optionRowIndicatorOverride(block),
              // Задание stylepack/text: свои отступ, рамка и жирность
              // плашки. Своего цвета выбора у этой строки нет — свой цвет
              // рамки ложится на все плашки. Пусто — {}.
              ...optionPaddingOverride(block, '10px', '14px'),
              // Задание stylepack/selected-look: своя рамка отмеченной
              // плашки, разница толщин — с отступа (раскладка не прыгает).
              ...optionBorderOverride(
                block,
                isSelected,
                {
                  width: '2px',
                  color: 'var(--color-border-primary, #f0f0f0)',
                },
                { y: '10px', x: '14px' },
              ),
              ...optionFontWeightOverride(block),
              // Задание stylepack/option-min-height: своя высота строки;
              // своего минимума у строки не было — пусто даёт {}.
              ...optionMinHeightOverride(block),
              // Задание stylepack/option-grid: ячейка сетки сжимается.
              ...optionCellOverride(block),
            }}
          >
            <input
              type="checkbox"
              name={block.id}
              value={opt.id}
              // WEB-1301 (F119): формат значения — id вариантов через
              // запятую, тот же, что у multiple_choice (PublicBlockRenderer.tsx
              // case 'multiple_choice') — статистика разбирает оба типа
              // одним кодом.
              checked={(values[block.id] ?? '')
                .split(',')
                .filter(Boolean)
                .includes(opt.id)}
              onChange={(e) => {
                const current = (values[block.id] ?? '')
                  .split(',')
                  .filter(Boolean);
                const next = e.target.checked
                  ? Array.from(new Set([...current, opt.id]))
                  : current.filter((id) => id !== opt.id);
                setValue(block.id, next.join(','));
              }}
              // WEB-1651-3: та же отметка, что у multiple_choice в
              // соседнем рендерере.
              style={{
                width: 18,
                height: 18,
                accentColor: resolveOptionAccent(block.optionAccentColor),
                // Задание stylepack/option-indicator-align: 'top' — у верха
                // строки; пусто — {}.
                ...optionIndicatorAlignOverride(block),
                // «Без отметки»: поле остаётся для клика, галочки нет.
                ...(optionIndicatorHidden(block)
                  ? OPTION_INDICATOR_HIDDEN_STYLE
                  : {}),
              }}
            />
            {/* Задание stylepack/option-grid: значки варианта. */}
            <OptionRowContent fields={block} icon={opt.icon}>
              {optionDisplay(opt)}
            </OptionRowContent>
          </label>
          );
        })}
      </div>
    </div>
  );
}
